import { z } from 'zod';

import {
  PaymentMethodSchema,
  PaymentStatusSchema,
  type PaymentMethod,
  type PaymentStatus,
} from '../common/enums';
import { Id, IsoDateTime, Paise } from '../common/primitives';

export { Paise } from '../common/primitives';
export { PaymentMethodSchema, PaymentStatusSchema };
export type { PaymentMethod, PaymentStatus };

export const PaymentProviderSchema = z.enum(['RAZORPAY', 'COD', 'WALLET']);
export type PaymentProvider = z.infer<typeof PaymentProviderSchema>;

export const RefundStatusSchema = z.enum(['PENDING', 'PROCESSED', 'FAILED']);
export type RefundStatus = z.infer<typeof RefundStatusSchema>;

// ── Response shapes ──────────────────────────────────────────────────────────

export const PaymentSchema = z.object({
  id: Id,
  orderGroupId: Id,
  provider: PaymentProviderSchema,
  method: PaymentMethodSchema.nullable(),
  status: PaymentStatusSchema,
  amountPaise: Paise,
  currency: z.literal('INR'),
  providerOrderId: z.string().nullable(),
  providerPaymentId: z.string().nullable(),
  failureReason: z.string().nullable(),
  authorizedAt: IsoDateTime.nullable(),
  capturedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type Payment = z.infer<typeof PaymentSchema>;

export const RefundSchema = z.object({
  id: Id,
  paymentId: Id,
  amountPaise: Paise,
  status: RefundStatusSchema,
  reason: z.string().nullable(),
  providerRefundId: z.string().nullable(),
  processedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type Refund = z.infer<typeof RefundSchema>;

/**
 * The "checkout init" envelope returned to the storefront — contains everything Razorpay's
 * client SDK needs to launch its modal, plus the local payment row id so the frontend can
 * call back to verify after the customer completes the gateway flow.
 */
export const CheckoutInitSchema = z.object({
  paymentId: Id,
  provider: PaymentProviderSchema,
  /// Razorpay key id (safe to expose to the browser).
  keyId: z.string().nullable(),
  providerOrderId: z.string().nullable(),
  /// What the gateway (or the courier, for COD) still has to collect.
  amountPaise: Paise,
  /// What the wallet covered. Zero unless `useWallet` was asked for.
  walletPaise: Paise.default(0),
  currency: z.literal('INR'),
  /// What the client should do next: open the gateway sheet, skip to
  /// confirmation (COD), or nothing at all because it is already paid (wallet).
  nextAction: z.enum(['OPEN_RAZORPAY_CHECKOUT', 'CONFIRM_COD', 'ALREADY_PAID']),
});
export type CheckoutInit = z.infer<typeof CheckoutInitSchema>;

// ── Request shapes ───────────────────────────────────────────────────────────

export const CreatePaymentRequest = z
  .object({
    orderGroupId: Id,
    provider: PaymentProviderSchema,
    /// Optional preferred method hint — Razorpay honours it as a default but the user can switch.
    method: PaymentMethodSchema.optional(),
    /**
     * Put the customer's spendable wallet balance towards this order and charge
     * the gateway only for what is left.
     *
     * How much that is, is the server's decision — reward money is capped at a
     * share of each order — so this is a yes/no, not an amount. Not available
     * with COD: the courier is told one figure to collect, and a part-paid order
     * would have them collect money the customer has already paid.
     */
    useWallet: z.boolean().optional(),
  })
  .strict();
export type CreatePaymentRequest = z.infer<typeof CreatePaymentRequest>;

/**
 * The three values Razorpay's client SDK returns on a successful payment. The server uses them
 * to verify the signature and capture the payment.
 */
export const VerifyPaymentRequest = z
  .object({
    razorpayOrderId: z.string().trim().min(1).max(64),
    razorpayPaymentId: z.string().trim().min(1).max(64),
    razorpaySignature: z.string().trim().min(1).max(256),
  })
  .strict();
export type VerifyPaymentRequest = z.infer<typeof VerifyPaymentRequest>;

export const CreateRefundRequest = z
  .object({
    paymentId: Id,
    /// Defaults to a full refund if omitted.
    amountPaise: Paise.optional(),
    reason: z.string().trim().min(2).max(500).optional(),
  })
  .strict();
export type CreateRefundRequest = z.infer<typeof CreateRefundRequest>;
